import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  LogOut, Users, Trophy, AlertTriangle, Search, ChevronRight,
  Inbox, Award, MapPin, ShieldCheck,
} from 'lucide-react';
import MessagesNavButton from '@/components/MessagesNavButton';
import XVAssistWidget from '@/components/XVAssistWidget';
import ModuleStripCard from '@/components/ModuleStripCard';
import BillingCard from '@/components/BillingCard';

const SA_PROVINCES = ["Eastern Cape","Western Cape","Gauteng","KwaZulu-Natal","Limpopo","Mpumalanga","North West","Free State","Northern Cape"];
const AGE_GROUPS = ["U13", "U14", "U15", "U16", "U18", "Academy", "Senior Pathway"];

export default function UnionDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [players, setPlayers] = useState([]);
  const [interestRequests, setInterestRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    age_group: 'all', province: 'all', position: '', needs_attention: false,
  });

  const fetchDashboard = useCallback(async () => {
    try {
      const [d, p, r] = await Promise.all([
        api.get('/union/dashboard'),
        api.get('/union/players'),
        api.get('/union/interest-requests'),
      ]);
      setDashboard(d.data);
      setPlayers(p.data);
      setInterestRequests(r.data);
    } catch (e) {
      toast.error('Failed to load union dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const applyFilters = async () => {
    const params = new URLSearchParams();
    if (filters.age_group !== 'all') params.set('age_group', filters.age_group);
    if (filters.province !== 'all') params.set('province', filters.province);
    if (filters.position) params.set('position', filters.position);
    if (filters.needs_attention) params.set('needs_attention', 'true');
    try {
      const res = await api.get(`/union/players?${params.toString()}`);
      setPlayers(res.data);
    } catch {
      toast.error('Filter failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-primary-neon text-xl">Loading union dashboard…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="union-dashboard">
      <nav className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="FutureXV" className="h-10" />
              <span className="text-zinc-600">|</span>
              <span className="font-heading font-bold">Union Dashboard</span>
              <Badge className="bg-purple-500/10 text-purple-300 border border-purple-500/30 text-[10px] uppercase tracking-wider">Governing Body</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate('/playbook')} className="border-zinc-700 text-zinc-300 mr-1" data-testid="playbook-nav-button">Playbook</Button>
              <MessagesNavButton />
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="union-logout-button">
                <LogOut className="w-4 h-4 mr-2" />Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold mb-2" data-testid="union-welcome">Welcome, {user?.full_name}</h1>
          <p className="text-zinc-400 font-body">Pathway-wide visibility across your affiliated schools, clubs and squads.</p>
        </div>

        <BillingCard />

        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="union-stats">
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-neon/10 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-primary-neon" /></div>
                <div><p className="text-zinc-400 text-xs">Total Players</p><p className="text-xl font-heading font-bold">{dashboard.total_players}</p></div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center"><Trophy className="w-5 h-5 text-blue-400" /></div>
                <div><p className="text-zinc-400 text-xs">Squads (Age Groups)</p><p className="text-xl font-heading font-bold">{dashboard.pathway_tiles?.length || 0}</p></div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-yellow-400" /></div>
                <div><p className="text-zinc-400 text-xs">Needs Attention</p><p className="text-xl font-heading font-bold">{dashboard.needs_attention_count}</p></div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center"><Inbox className="w-5 h-5 text-purple-400" /></div>
                <div><p className="text-zinc-400 text-xs">Open Interest Requests</p><p className="text-xl font-heading font-bold">{dashboard.open_requests}</p></div>
              </div>
            </Card>
          </div>
        )}

        <Tabs defaultValue="pathway" className="w-full">
          <div className="mb-6"><ModuleStripCard role="union" /></div>
          <TabsList className="grid w-full grid-cols-3 mb-6 bg-zinc-900 max-w-xl" data-testid="union-tabs">
            <TabsTrigger value="pathway" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="pathway-tab">
              <Trophy className="w-4 h-4 mr-2" />Pathway
            </TabsTrigger>
            <TabsTrigger value="players" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="players-tab">
              <Users className="w-4 h-4 mr-2" />Players
            </TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="requests-tab">
              <Inbox className="w-4 h-4 mr-2" />Requests
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pathway">
            <h2 className="text-2xl font-heading font-bold mb-4">Pathway Overview</h2>
            <p className="text-zinc-400 text-sm mb-6">One tile per age group. Average PDS, injury flags and consistency populate as the wellness and PDS engines come online.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {(dashboard?.pathway_tiles || []).map((t) => (
                <Card key={t.age_group} className="bg-zinc-900 border-zinc-800 p-5 hover:border-primary-neon/40 transition-colors cursor-pointer" data-testid={`pathway-tile-${t.age_group}`}>
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-heading font-bold text-lg">{t.age_group}</h3>
                    <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700">{t.squad_size} players</Badge>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-zinc-500">Avg PDS</span><span className="text-zinc-300 font-mono">{t.avg_pds || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Injury flags</span><span className="text-zinc-300 font-mono">{t.injury_flags}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Consistency</span><span className="text-zinc-300 font-mono">{t.avg_consistency_pct ? `${t.avg_consistency_pct}%` : '—'}</span></div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="players">
            <Card className="bg-zinc-900 border-zinc-800 p-5 mb-4" data-testid="union-filters">
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Select value={filters.age_group} onValueChange={(v) => setFilters({...filters, age_group: v})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-10" data-testid="filter-age-group"><SelectValue placeholder="Age group" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                    <SelectItem value="all">All age groups</SelectItem>
                    {AGE_GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.province} onValueChange={(v) => setFilters({...filters, province: v})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-10" data-testid="filter-province"><SelectValue placeholder="Province" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                    <SelectItem value="all">All provinces</SelectItem>
                    {SA_PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  value={filters.position}
                  onChange={(e) => setFilters({...filters, position: e.target.value})}
                  placeholder="Position"
                  className="bg-zinc-950 border-zinc-800 text-white h-10"
                  data-testid="filter-position"
                />
                <label className="flex items-center gap-2 text-sm text-zinc-300 px-3">
                  <input
                    type="checkbox"
                    checked={filters.needs_attention}
                    onChange={(e) => setFilters({...filters, needs_attention: e.target.checked})}
                    className="accent-[#A3FF00]"
                    data-testid="filter-needs-attention"
                  />
                  Needs attention only
                </label>
                <Button onClick={applyFilters} className="btn-primary" data-testid="apply-filters-button">
                  <Search className="w-4 h-4 mr-2" />Apply
                </Button>
              </div>
            </Card>

            {players.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800 p-12 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
                <p className="text-zinc-500">No players match your filters yet.</p>
              </Card>
            ) : (
              <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="union-players-table">
                    <thead className="bg-zinc-950 border-b border-zinc-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs text-zinc-400 uppercase">Player</th>
                        <th className="px-4 py-3 text-left text-xs text-zinc-400 uppercase">Age Group</th>
                        <th className="px-4 py-3 text-left text-xs text-zinc-400 uppercase">Position</th>
                        <th className="px-4 py-3 text-left text-xs text-zinc-400 uppercase">Province</th>
                        <th className="px-4 py-3 text-left text-xs text-zinc-400 uppercase">School/Club</th>
                        <th className="px-4 py-3 text-left text-xs text-zinc-400 uppercase">PDS</th>
                        <th className="px-4 py-3 text-left text-xs text-zinc-400 uppercase">TRI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {players.map((p) => (
                        <tr key={p.player_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`union-row-${p.player_id}`}>
                          <td className="px-4 py-3 text-sm text-white font-semibold">{p.name}</td>
                          <td className="px-4 py-3 text-sm text-zinc-400">{p.age_group || '—'}</td>
                          <td className="px-4 py-3 text-sm text-zinc-400">{p.position || '—'}</td>
                          <td className="px-4 py-3 text-sm text-zinc-400">{p.province || '—'}</td>
                          <td className="px-4 py-3 text-sm text-zinc-400">{p.school_club || '—'}</td>
                          <td className="px-4 py-3 text-sm text-zinc-300 font-mono">{p.pds ?? '—'}</td>
                          <td className="px-4 py-3 text-sm text-zinc-300 font-mono">{p.tri ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="requests">
            <h2 className="text-2xl font-heading font-bold mb-4">Player Interest Requests</h2>
            <p className="text-zinc-400 text-sm mb-6">Formal requests sent to affiliated schools and clubs. Rate-limited to 20 per 30 days per age group (POPIA-compliant audit trail).</p>
            {interestRequests.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800 p-12 text-center">
                <Inbox className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
                <p className="text-zinc-500">No interest requests yet.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {interestRequests.map((r) => (
                  <Card key={r.id} className="bg-zinc-900 border-zinc-800 p-4 flex items-center justify-between" data-testid={`request-${r.id}`}>
                    <div>
                      <p className="font-semibold">Player: {r.player_id}</p>
                      <p className="text-zinc-400 text-sm">Programme: {r.programme_level}</p>
                      {r.note && <p className="text-zinc-500 text-xs mt-1">{r.note}</p>}
                    </div>
                    <Badge className={
                      r.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' :
                      r.status === 'accepted' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                      'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20'
                    }>{r.status}</Badge>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <XVAssistWidget />
    </div>
  );
}
